import { Boxes } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { WorkspaceSidebarFooter } from '../../../forge/components/layout/WorkspaceSidebarFooter';
import { ForgeTheme } from '../../../forge/hooks/useForgeTheme';
import { useArchonAccess } from '../../context/ArchonAccessContext';
import { ARCHON_NAV_SECTIONS } from './archonNavigation';

interface ArchonSidebarProps {
  theme: ForgeTheme;
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  /** Takes the sidebar off the page entirely; the layout's reveal handle brings it back. */
  hideSidebar: () => void;
  onDragStart: (event: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  isMobile?: boolean;
  onMobileNavigate?: () => void;
}

export const ArchonSidebar: React.FC<ArchonSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  hideSidebar,
  onDragStart,
  toggleTheme,
  currentPath,
  isMobile = false,
  onMobileNavigate,
}) => {
  const { canManageData } = useArchonAccess();
  const effectiveCollapsed = isCollapsed && !isMobile;
  const sidebarClasses = [
    'forge-sidebar',
    'archon-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Archon navigation"
    >
      <div className="archon-sidebar-brand">
        <Boxes aria-hidden="true" />
        <span>Archon</span>
      </div>
      <nav className="sidebar-nav" aria-label="Archon navigation">
        {ARCHON_NAV_SECTIONS.map((section) => (
          <div key={section.title} className="sidebar-section">
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => {
              if (item.ownerOnly && !canManageData) return null;
              const Icon = item.icon;
              const isActive = currentPath === item.path || currentPath.startsWith(`${item.path}/`);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  title={effectiveCollapsed ? item.label : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={onMobileNavigate}
                >
                  <Icon aria-hidden="true" />
                  <span className="sidebar-item-label">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <WorkspaceSidebarFooter
        theme={theme}
        toggleTheme={toggleTheme}
        collapsed={effectiveCollapsed}
        isMobile={isMobile}
        hideSidebar={hideSidebar}
        onMobileNavigate={onMobileNavigate}
      />
      {!isMobile ? <div className="sidebar-drag-handle" onMouseDown={onDragStart} /> : null}
    </aside>
  );
};
